import { type JSX } from 'react';

import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';
import { Textarea } from '@/components/ui/textarea.js';

/** The values a person edits about a connection, in one shape both dialogs pass around. */
export interface ConnectionDetailsValue {
  label: string;
  usageHint: string;
}

/** An empty set of details — what a brand-new connection starts from. */
export const EMPTY_CONNECTION_DETAILS: ConnectionDetailsValue = {
  label: '',
  usageHint: '',
};

/**
 * Name and agent instruction for one connection — the fields the connect form and an
 * existing connection's settings both ask for, in one place so the two never word the same question
 * differently.
 *
 * `scopeNote` is what the caller says about WHERE these values apply, and it is not optional by
 * accident: in a working workspace a rename touches that workspace's profile alone, and somebody who
 * is not told that believes they renamed the connection everywhere.
 *
 * `labelNote` and `usageHintNote` say where the value currently on screen CAME FROM — see
 * `connection-value-origin.ts`. They are absent on a connection being created, which has no value to
 * have inherited anything.
 */
export function ConnectionDetailsFields({
  idPrefix,
  providerName,
  value,
  onChange,
  scopeNote,
  labelNote,
  usageHintNote,
  required = false,
  disabled = false,
}: {
  idPrefix: string;
  providerName: string;
  value: ConnectionDetailsValue;
  onChange: (next: ConnectionDetailsValue) => void;
  scopeNote: string;
  labelNote?: string | null;
  usageHintNote?: string | null;
  required?: boolean;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-label`}>
          Name{required ? '' : ' (optional)'}
        </Label>
        <Input
          id={`${idPrefix}-label`}
          value={value.label}
          disabled={disabled}
          placeholder={`${providerName} — what is this one for?`}
          onChange={(event) =>
            onChange({ ...value, label: event.target.value })
          }
        />
        <p className="text-caption leading-relaxed text-muted-foreground">
          What you call this connection, so you can tell it apart from your
          others.
        </p>
        {labelNote && (
          <p className="text-caption leading-relaxed text-muted-foreground">
            {labelNote}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-usage-hint`}>
          When to use it{required ? '' : ' (optional)'}
        </Label>
        <Textarea
          id={`${idPrefix}-usage-hint`}
          value={value.usageHint}
          disabled={disabled}
          rows={3}
          maxLength={500}
          placeholder="The agent reads this to decide which connection to reach for."
          onChange={(event) =>
            onChange({ ...value, usageHint: event.target.value })
          }
        />
        <p className="text-caption leading-relaxed text-muted-foreground">
          {scopeNote}
        </p>
        {usageHintNote && (
          <p className="text-caption leading-relaxed text-muted-foreground">
            {usageHintNote}
          </p>
        )}
      </div>
    </div>
  );
}
